import { useTranslation } from 'react-i18next';

import { mapVirtualToActualDocumentType } from 'services/documentTypes/documentTypes';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import {
  downloadAsCSV,
  downloadAsPDF,
  downloadReceiverEnvelope,
} from 'services/repositories/transactions/transactions';
import { useWebEDIStore } from 'stores/webedi/webedi';
import { useOCRAvailability } from 'support/helpers/ocr/ocr';
import { Connectors, DocumentType, DocumentTypeWithVirtual, TransactionOriginApp, ValueOf } from 'support/types';

import { useConfirmOrderAction } from './actions/confirmOrderAction';
import { useForceDocumentProcessingAction } from './actions/forceDocumentProcessingAction';
import { useDocumentActionWithOCR } from './actions/helpers';
import { useRejectOrderAction } from './actions/rejectOrderAction';
import { DestructiveAction } from './components/DestructiveAction/DestructiveAction';
import { PrimaryAction } from './components/PrimaryAction/PrimaryAction';
import { SecondaryActions } from './components/SecondaryAction/SecondaryActions';
import { ActionConfig, DocumentTypeActionConfig, GetActionsReturn, MessageV2DTO } from './components/types';

type ActionButtonsProps = {
  message: MessageV2DTO;
  messageType: ValueOf<DocumentTypeWithVirtual>;
  messageOriginApp: TransactionOriginApp;
  relatedTransactions: Array<MessageV2DTO> | undefined;
  enabledDocumentTypes: Array<DocumentType>;
};

export function ActionButtons({
  message,
  relatedTransactions,
  enabledDocumentTypes,
  messageType,
  messageOriginApp,
}: ActionButtonsProps) {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const destinationProcess = useWebEDIStore((state) => state.destinationProcess);

  // Set up OCR availability hooks for various document types
  const orderResponseOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.orderResponse,
  });
  const shippingNoticeOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.shippingNotice,
  });
  const invoiceOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.invoice,
  });
  const creditNoteOCRAvailability = useOCRAvailability({
    fromDocument: message,
    toDocumentType: DocumentType.creditNote,
  });

  // Create actions using custom hooks
  const createOrderResponseAction: ActionConfig = useConfirmOrderAction(
    message,
    DocumentType.orderResponse,
    relatedTransactions,
    orderResponseOCRAvailability,
    {
      label: t('transactions:show.actionButtons.createOrderResponse'),
      analyticsId: 'transactions:confirm_order',
    },
  );

  const rejectOrderAction: ActionConfig = useRejectOrderAction(
    message,
    DocumentType.orderResponse,
    relatedTransactions,
    createOrderResponseAction.test,
  );

  const forceDocumentProcessingAction: ActionConfig = useForceDocumentProcessingAction(message);

  const createShippingNoticeAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.shippingNotice,
    relatedTransactions,
    shippingNoticeOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createShippingNotice',
      analyticsId: 'transactions:create_shipping_notice',
    },
  );

  const createInvoiceAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.invoice,
    relatedTransactions,
    invoiceOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createInvoice',
      analyticsId: 'transactions:create_invoice',
    },
  );

  const createCreditNoteAction: ActionConfig = useDocumentActionWithOCR(
    message,
    DocumentType.creditNote,
    relatedTransactions,
    creditNoteOCRAvailability,
    {
      label: 'transactions:show.actionButtons.createCreditNote',
      analyticsId: 'transactions:create_credit_note',
    },
  );

  // Simple actions that don't require modals
  const downloadAsCSVAction: ActionConfig = {
    label: 'transactions:show.actionButtons.downloadAsCSV',
    secondaryOptionOnly: true,
    analyticsId: 'transactions:download_as_csv',
    onClick: () => downloadAsCSV(message),
  };

  const downloadAsPDFAction: ActionConfig = {
    label: 'transactions:show.actionButtons.downloadAsPDF',
    secondaryOptionOnly: true,
    analyticsId: 'transactions:download_as_pdf',
    onClick: () => downloadAsPDF(message),
    test: () => {
      const result = 'UNATTENDED_OCR' !== messageOriginApp && 'PORTAL_OCR' !== messageOriginApp;
      return result;
    },
  };

  const downloadAsReceiverEnvelopeRawPayload: ActionConfig = {
    label:
      'transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload' +
      (destinationProcess?.configuration?.normalizer ? `_${destinationProcess?.configuration?.normalizer}` : ''),
    onClick: (message: MessageV2DTO) => {
      if (!message.receiver_envelope) {
        return;
      }

      return downloadReceiverEnvelope(message.receiver_envelope.id);
    },
    secondaryOptionOnly: true,
    analyticsId: 'transactions:download_receiver_envelope_raw_payload',
    test: (message: MessageV2DTO) => {
      const { receiver_envelope: receiverEnvelope, receiver_partner: receiverPartner } = message;

      if (!receiverEnvelope) {
        return false;
      }

      if (receiverEnvelope.receiverIntegration.connector === Connectors.WEB_EDI) {
        return false;
      }

      return receiverPartner?.id === currentPartner?.id;
    },
  };

  // Define document type actions with integrated OCR actions
  const orderActions: DocumentTypeActionConfig = {
    documentType: DocumentType.order,
    actions: [
      createOrderResponseAction,
      createShippingNoticeAction,
      createInvoiceAction,
      downloadAsPDFAction,
      downloadAsCSVAction,
      rejectOrderAction,
    ],
  };

  const orderResponseActions: DocumentTypeActionConfig = {
    documentType: DocumentType.orderResponse,
    actions: [createShippingNoticeAction, createInvoiceAction, downloadAsPDFAction],
  };

  const shippingNoticeActions: DocumentTypeActionConfig = {
    documentType: DocumentType.shippingNotice,
    actions: [createInvoiceAction, downloadAsPDFAction],
  };

  const invoiceActions: DocumentTypeActionConfig = {
    documentType: DocumentType.invoice,
    actions: [createCreditNoteAction, downloadAsPDFAction],
  };

  const creditNoteActions: DocumentTypeActionConfig = {
    documentType: DocumentType.creditNote,
    actions: [downloadAsPDFAction],
  };

  const documentActions: Array<DocumentTypeActionConfig> = [
    orderActions,
    orderResponseActions,
    shippingNoticeActions,
    invoiceActions,
    creditNoteActions,
  ];

  const allDocumentsActions = [downloadAsReceiverEnvelopeRawPayload, forceDocumentProcessingAction];
  const actions: Array<ActionConfig> =
    documentActions
      .find(
        (action: DocumentTypeActionConfig) =>
          action.documentType === mapVirtualToActualDocumentType(messageType as DocumentTypeWithVirtual),
      )
      ?.actions?.concat(allDocumentsActions) ?? allDocumentsActions;

  const availableActions = actions
    // Only actions for enabled document types or without a document type
    .filter(
      (action: ActionConfig) =>
        typeof action.documentType === 'undefined' || enabledDocumentTypes.includes(action.documentType),
    )
    // Only actions that pass their test or without a test
    .filter((action: ActionConfig) => !action.test || action.test(message));

  const { primaryAction, secondaryActions, destructiveAction } = availableActions.reduce(
    (acc: GetActionsReturn, action) => {
      if (action.type === 'destructive' && !acc.destructiveAction) {
        return {
          ...acc,
          destructiveAction: action,
        };
      }

      if (!acc.primaryAction && !action.secondaryOptionOnly && !action.documentTypeExists) {
        return {
          ...acc,
          primaryAction: action,
        };
      }

      return {
        ...acc,
        secondaryActions: [...acc.secondaryActions, action],
      };
    },
    { primaryAction: null, secondaryActions: [], destructiveAction: null },
  );

  return (
    <div className="flex flex-row items-center">
      <SecondaryActions actions={secondaryActions} fromDocument={message} />
      <DestructiveAction action={destructiveAction} fromDocument={message} />
      <PrimaryAction action={primaryAction} fromDocument={message} />

      {/* Render modals from actions */}
      {primaryAction?.renderModal?.(message)}
      {destructiveAction?.renderModal?.(message)}
      {secondaryActions.map((action) => action.renderModal?.(message))}
    </div>
  );
}

/**
 * i18n
 * t('transactions:show.actionButtons.createOrderResponse')
 * t('transactions:show.actionButtons.createShippingNotice')
 * t('transactions:show.actionButtons.createCreditNote')
 * t('transactions:show.actionButtons.createInvoice')
 *
 * t('transactions:show.actionButtons.downloadAsPDF')
 * t('transactions:show.actionButtons.downloadAsCSV')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_EDIFACT')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_EDIFACT_V2')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_PHBEST')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_FIXED')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_TRADACOMS')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_XML')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_EXCEL')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_CSV')
 * t('transactions:show.actionButtons.downloadReceiverEnvelopeRawPayload_NONE')
 *
 */
